/**
 * /work-orders — bulk work order printing (tracker item 58).
 *
 * Without ?batchId: pick one day's SCHEDULED / WORK_ORDER_GENERATED jobs
 * (individually, all for the day, or all for one technician, resolved on the
 * server across every page), set service / billing copies per job and prepare
 * a batch; below, the searchable batch history.
 *
 * With ?batchId=: the batch preview. Each copy prints on a fresh sheet from
 * the Job's CURRENT details; this page's controls and the app navigation are
 * hidden on paper. Nothing changes a Job until staff, after the print dialog,
 * ticks the jobs that actually printed and confirms them.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient } from '../api';
import * as types from '../api/types';
import { CompanyFooterNote, CompanyLetterhead } from '../components/PrintLayout';
import { inputClass, labelClass, primaryButtonClass, rowActionClass, secondaryButtonClass } from '../components/formStyles';
import { getErrorMessage } from '../utils/apiError';
import { JobEnrollmentChip } from '../features/service-bundles/EnrollmentChip';
import { formatJobDate } from '../utils/jobPresentation';
import { jobStatusLabel } from './jobUi';
import { ServiceOrderDocument } from './print/ServiceOrderDocument';
import {
  WorkOrderSelectionMap,
  addToSelection,
  batchStatusLabel,
  compareCandidates,
  confirmBlockMessage,
  confirmRequest,
  confirmableInRun,
  confirmationSummary,
  defaultPrintRun,
  expandSheets,
  groupByRouteAndTechnician,
  jobLabel,
  memberGroupKey,
  pluralize,
  prepareRequest,
  printBlockers,
  printedVersions,
  problemsFromError,
  removeFromSelection,
  selectionEntryError,
  selectionTotals,
  setCopies,
  technicianLabel,
  timeLabel,
} from './workOrdersUi';

const CANDIDATE_PAGE_SIZE = 50;
const HISTORY_PAGE_SIZE = 10;
/** Longest wait for the logo before Print is enabled without it. */
const LOGO_WAIT_MS = 3000;

const sectionClass = 'rounded-lg bg-white p-4 shadow sm:p-5';
const badgeClass = 'inline-block rounded px-1.5 py-0.5 text-xs font-semibold';

export function WorkOrdersPage() {
  const [params] = useSearchParams();
  const batchId = params.get('batchId');
  if (batchId) return <WorkOrderBatchView key={batchId} batchId={batchId} />;
  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <h1 className="text-2xl font-bold text-gray-900">Work Orders</h1>
      <p className="mt-1 mb-4 max-w-3xl text-sm text-gray-600">
        Print one day&apos;s work orders: a service copy without prices and a billing copy with them. Printing does not change any job;
        afterwards you confirm which jobs actually printed, and only those move from Scheduled to Work Order Generated.
      </p>
      <WorkOrderPlanner />
      <WorkOrderHistory />
    </main>
  );
}

function ProblemList({ problems, title }: { problems: types.WorkOrderJobProblem[]; title: string }) {
  if (problems.length === 0) return null;
  return (
    <div role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
      <p className="font-semibold">{title}</p>
      <ul className="mt-1 list-disc space-y-1 pl-5">
        {problems.map((p) => (
          <li key={`${p.jobId}:${p.reason}`}>
            <Link to={`/jobs/${encodeURIComponent(p.jobId)}`} className="font-semibold underline">{p.label || `Job ${p.jobId}`}</Link>: {p.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selection and preparation
// ---------------------------------------------------------------------------

interface CandidateFilters {
  routeId: string;
  assignedTo: string;
  status: string;
  search: string;
}

const NO_FILTERS: CandidateFilters = { routeId: '', assignedTo: '', status: '', search: '' };

function WorkOrderPlanner() {
  const api = useMemo(() => createAPIClient(), []);
  const [, setParams] = useSearchParams();
  const [date, setDate] = useState<string | null>(null);
  const [filters, setFilters] = useState<CandidateFilters>(NO_FILTERS);
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState<types.WorkOrderCandidatePage | null>(null);
  const [rows, setRows] = useState<types.WorkOrderCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selection, setSelection] = useState<WorkOrderSelectionMap>(new Map());
  /** The latest selection, for async handlers (a resolved "select all" merges into it). */
  const selectionRef = useRef<WorkOrderSelectionMap>(selection);
  selectionRef.current = selection;
  const [technician, setTechnician] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [problems, setProblems] = useState<types.WorkOrderJobProblem[]>([]);
  const [busy, setBusy] = useState(false);
  const sequence = useRef(0);
  /** The date the server chose on first load (its candidates are already shown). */
  const initialDate = useRef<string | null>(null);

  const load = useCallback(async (cursor?: string) => {
    const mine = ++sequence.current;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await api.listWorkOrderCandidates({
        date: date ?? undefined,
        routeId: filters.routeId || undefined,
        assignedTo: filters.assignedTo || undefined,
        status: (filters.status || undefined) as types.WorkOrderEligibleStatus | undefined,
        search: filters.search || undefined,
        cursor,
        limit: CANDIDATE_PAGE_SIZE,
      });
      if (mine !== sequence.current) return;
      if (!date) {
        initialDate.current = result.date;
        setDate(result.date);
      }
      setPage(result);
      setRows((previous) => (cursor ? [...previous, ...result.data] : result.data));
    } catch (error) {
      if (mine === sequence.current) setLoadError(getErrorMessage(error, 'Failed to load jobs'));
    } finally {
      if (mine === sequence.current) setLoading(false);
    }
  }, [api, date, filters]);

  useEffect(() => {
    // The first response chose the date; its jobs are already listed.
    if (date && initialDate.current === date) {
      initialDate.current = null;
      return;
    }
    void load();
  }, [load, date]);

  const changeDate = (next: string) => {
    if (!next || next === date) return;
    if (selection.size > 0) {
      setSelection(new Map());
      setNotice(`Selection cleared: a batch covers one date only. Now showing ${formatJobDate(next)}.`);
    } else {
      setNotice(null);
    }
    setProblems([]);
    initialDate.current = null;
    setDate(next);
  };

  const toggle = (candidate: types.WorkOrderCandidate) => {
    setProblems([]);
    setSelection((current) => (current.has(candidate.id) ? removeFromSelection(current, [candidate.id]) : addToSelection(current, [candidate]).selection));
  };

  const selectAll = async (scope: 'DAY' | 'TECHNICIAN') => {
    if (!date) return;
    const requestedDate = date;
    setBusy(true);
    setActionError(null);
    setProblems([]);
    try {
      const result = await api.resolveWorkOrderSelection(requestedDate, scope, scope === 'TECHNICIAN' ? technician : undefined);
      // The date changed while this was loading: never mix dates
      if (result.date !== requestedDate) return;
      const next = addToSelection(selectionRef.current, result.jobs);
      selectionRef.current = next.selection;
      setSelection(next.selection);
      const facet = page?.technicians.find((t) => t.id === technician);
      const who = scope === 'DAY' ? `for ${formatJobDate(result.date)}` : `for ${facet?.label ?? technicianLabel(null)}`;
      setNotice(`Found ${pluralize(result.total, 'eligible job')} ${who}; ${pluralize(next.added, 'job')} added to the selection. Review it below before preparing.`);
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to select jobs'));
    } finally {
      setBusy(false);
    }
  };

  const prepare = async () => {
    if (!date) return;
    setBusy(true);
    setActionError(null);
    setProblems([]);
    try {
      const batch = await api.prepareWorkOrderBatch(prepareRequest(date, selection));
      setParams({ batchId: batch.id });
    } catch (error) {
      setActionError(getErrorMessage(error, 'Failed to prepare work orders'));
      setProblems(problemsFromError(error));
    } finally {
      setBusy(false);
    }
  };

  const totals = selectionTotals(selection);
  const selected = Array.from(selection.values()).sort((a, b) => compareCandidates(a.candidate, b.candidate));
  const selectedGroups = groupByRouteAndTechnician(selected, (entry) => entry.candidate);
  const candidateGroups = groupByRouteAndTechnician(rows, (row) => row);

  return (
    <>
      <section aria-label="Choose jobs" className={`${sectionClass} mb-6`}>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label htmlFor="wo-date" className={labelClass}>Scheduled date</label>
            <input id="wo-date" type="date" className={inputClass} value={date ?? ''} onChange={(e) => changeDate(e.target.value)} />
            {page && <p className="mt-1 text-xs text-gray-500">Company time zone: {page.timezone}</p>}
          </div>
          <div>
            <label htmlFor="wo-route" className={labelClass}>Route</label>
            <select id="wo-route" className={inputClass} value={filters.routeId} onChange={(e) => setFilters((f) => ({ ...f, routeId: e.target.value }))}>
              <option value="">All routes</option>
              {page?.routes.map((r) => <option key={r.id} value={r.id}>{r.label} ({r.count})</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="wo-tech" className={labelClass}>Technician</label>
            <select id="wo-tech" className={inputClass} value={filters.assignedTo} onChange={(e) => setFilters((f) => ({ ...f, assignedTo: e.target.value }))}>
              <option value="">All technicians</option>
              {page?.technicians.map((t) => <option key={t.id} value={t.id}>{t.label} ({t.count})</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="wo-status" className={labelClass}>Status</label>
            <select id="wo-status" className={inputClass} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              <option value="">Scheduled and generated</option>
              <option value="SCHEDULED">Scheduled</option>
              <option value="WORK_ORDER_GENERATED">Work Order Generated</option>
            </select>
          </div>
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, search: searchDraft.trim() })); }}
          >
            <div>
              <label htmlFor="wo-search" className={labelClass}>Search</label>
              <input id="wo-search" type="search" className={inputClass} placeholder="Customer, address or job id" value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)} />
            </div>
            <button type="submit" className={secondaryButtonClass}>Search</button>
          </form>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-gray-200 pt-4">
          <button type="button" className={secondaryButtonClass} disabled={busy || !date} onClick={() => void selectAll('DAY')}>
            Select all for day{page ? ` (${page.dayTotal})` : ''}
          </button>
          <div className="flex items-end gap-2">
            <div>
              <label htmlFor="wo-select-tech" className={labelClass}>Technician to select</label>
              <select id="wo-select-tech" className={inputClass} value={technician} onChange={(e) => setTechnician(e.target.value)}>
                <option value="">Choose…</option>
                {page?.technicians.map((t) => <option key={t.id} value={t.id}>{t.label} ({t.count})</option>)}
              </select>
            </div>
            <button type="button" className={secondaryButtonClass} disabled={busy || !date || !technician} onClick={() => void selectAll('TECHNICIAN')}>
              Select all for technician
            </button>
          </div>
        </div>

        {notice && <p role="status" className="mt-3 text-sm text-blue-900">{notice}</p>}
        {loadError && (
          <div role="alert" className="mt-3 text-sm text-red-800">
            {loadError} <button type="button" className={rowActionClass} onClick={() => void load()}>Retry</button>
          </div>
        )}

        <div className="mt-4">
          {page && (
            <p className="mb-2 text-sm text-gray-600">
              {formatJobDate(page.date)}: showing {rows.length} of {pluralize(page.total, 'matching job')} ({page.dayTotal} eligible that day).
            </p>
          )}
          {loading && rows.length === 0 && <p role="status" className="text-gray-600">Loading jobs…</p>}
          {!loading && page && rows.length === 0 && <p className="text-gray-600">No scheduled jobs match.</p>}
          {candidateGroups.map((group) => (
            <div key={group.key} className="mb-3">
              <h3 className="text-sm font-semibold text-gray-800">{group.route} · {group.technician}</h3>
              <ul className="mt-1 divide-y divide-gray-100 rounded border border-gray-200">
                {group.items.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                    <input
                      id={`wo-pick-${c.id}`}
                      type="checkbox"
                      className="h-5 w-5"
                      checked={selection.has(c.id)}
                      onChange={() => toggle(c)}
                      aria-label={`Select ${c.customer.name}, ${c.property.address}`}
                    />
                    <label htmlFor={`wo-pick-${c.id}`} className="min-w-0 flex-1 break-words">
                      <span className="font-medium">{c.customer.name}</span>
                      <span className="text-gray-600"> · {c.property.address}{c.property.city ? `, ${c.property.city}` : ''}</span>
                    </label>
                    <span className="text-gray-600">{timeLabel(c.scheduledStartTime)}</span>
                    <span className="text-gray-600">{jobStatusLabel(c.status)}</span>
                    <JobEnrollmentChip job={c} />
                    {c.previouslyConfirmed && <span className={`${badgeClass} bg-amber-100 text-amber-900`}>Printed before</span>}
                    {!c.hasWorkItems && <span className={`${badgeClass} bg-red-100 text-red-800`}>No work items</span>}
                    <Link to={`/jobs/${encodeURIComponent(c.id)}`} className={rowActionClass}>Job</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {page?.nextCursor && (
            <button type="button" className={secondaryButtonClass} disabled={loading} onClick={() => void load(page.nextCursor!)}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </section>

      <section aria-label="Selected jobs" className={`${sectionClass} mb-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Selected ({totals.jobs})</h2>
          <p className="text-sm text-gray-700" data-testid="selection-totals">
            {pluralize(totals.jobs, 'job')} · {pluralize(totals.serviceCopies, 'service copy', 'service copies')} · {pluralize(totals.billingCopies, 'billing copy', 'billing copies')}
          </p>
        </div>
        {selected.length === 0 ? (
          <p className="mt-2 text-sm text-gray-600">Select jobs above. Each job starts with one service copy and one billing copy.</p>
        ) : (
          <>
            <p className="mt-1 text-sm text-gray-600">Print order: route, then technician, then start time.</p>
            {selectedGroups.map((group) => (
              <div key={group.key} className="mt-3">
                <h3 className="text-sm font-semibold text-gray-800">{group.route} · {group.technician}</h3>
                <ul className="mt-1 divide-y divide-gray-100 rounded border border-gray-200">
                  {group.items.map((entry) => {
                    const c = entry.candidate;
                    const error = selectionEntryError(entry);
                    return (
                      <li key={c.id} className="px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="min-w-0 flex-1 break-words">
                            <span className="font-medium">{c.customer.name}</span>
                            <span className="text-gray-600"> · {c.property.address} · {timeLabel(c.scheduledStartTime)}</span>
                          </span>
                          <label className="flex items-center gap-1">
                            <span>Service</span>
                            <input
                              type="number" min={0} max={types.MAX_WORK_ORDER_COPIES} step={1} inputMode="numeric"
                              className="w-16 rounded border border-gray-300 px-2 py-1"
                              aria-label={`Service copies for ${c.customer.name}`}
                              value={Number.isNaN(entry.serviceCopies) ? '' : entry.serviceCopies}
                              onChange={(e) => setSelection((s) => setCopies(s, c.id, 'SERVICE', e.target.value))}
                            />
                          </label>
                          <label className="flex items-center gap-1">
                            <span>Billing</span>
                            <input
                              type="number" min={0} max={types.MAX_WORK_ORDER_COPIES} step={1} inputMode="numeric"
                              className="w-16 rounded border border-gray-300 px-2 py-1"
                              aria-label={`Billing copies for ${c.customer.name}`}
                              value={Number.isNaN(entry.billingCopies) ? '' : entry.billingCopies}
                              onChange={(e) => setSelection((s) => setCopies(s, c.id, 'BILLING', e.target.value))}
                            />
                          </label>
                          <button
                            type="button" className={rowActionClass}
                            aria-label={`Remove ${c.customer.name}`}
                            onClick={() => setSelection((s) => removeFromSelection(s, [c.id]))}
                          >
                            Remove
                          </button>
                        </div>
                        {error && <p className="mt-1 text-red-700">{error}</p>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </>
        )}
        {actionError && <p role="alert" className="mt-3 text-sm text-red-800">{actionError}</p>}
        <ProblemList problems={problems} title="Nothing was prepared. Fix or remove these jobs:" />
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button" className={primaryButtonClass}
            disabled={busy || totals.jobs === 0 || totals.invalid > 0 || !date}
            onClick={() => void prepare()}
          >
            {busy ? 'Working…' : `Prepare ${pluralize(totals.jobs, 'work order')}`}
          </button>
          {selection.size > 0 && (
            <button type="button" className={secondaryButtonClass} onClick={() => { setSelection(new Map()); setProblems([]); }}>
              Clear selection
            </button>
          )}
        </div>
      </section>
    </>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

function WorkOrderHistory() {
  const api = useMemo(() => createAPIClient(), []);
  const [query, setQuery] = useState<types.WorkOrderBatchListQuery>({ page: 1, limit: HISTORY_PAGE_SIZE });
  const [searchDraft, setSearchDraft] = useState('');
  const [result, setResult] = useState<types.WorkOrderBatchList | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setError(null);
    api.listWorkOrderBatches(query)
      .then((list) => { if (active) setResult(list); })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Failed to load work order history')); });
    return () => { active = false; };
  }, [api, query]);

  const page = query.page ?? 1;
  return (
    <section aria-label="Work order history" className={sectionClass}>
      <h2 className="text-lg font-semibold text-gray-900">History</h2>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="wo-history-date" className={labelClass}>Date</label>
          <input id="wo-history-date" type="date" className={inputClass} value={query.date ?? ''} onChange={(e) => setQuery((q) => ({ ...q, page: 1, date: e.target.value || undefined }))} />
        </div>
        <div>
          <label htmlFor="wo-history-status" className={labelClass}>State</label>
          <select id="wo-history-status" className={inputClass} value={query.status ?? ''} onChange={(e) => setQuery((q) => ({ ...q, page: 1, status: (e.target.value || undefined) as types.WorkOrderBatchStatus | undefined }))}>
            <option value="">Any</option>
            <option value="PREPARED">Prepared</option>
            <option value="PARTIALLY_CONFIRMED">Partially confirmed</option>
            <option value="CONFIRMED">Confirmed</option>
          </select>
        </div>
        <form className="flex items-end gap-2" onSubmit={(e) => { e.preventDefault(); setQuery((q) => ({ ...q, page: 1, search: searchDraft.trim() || undefined })); }}>
          <div>
            <label htmlFor="wo-history-search" className={labelClass}>Search</label>
            <input id="wo-history-search" type="search" className={inputClass} placeholder="Customer, address, job id or staff email" value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)} />
          </div>
          <button type="submit" className={secondaryButtonClass}>Search</button>
        </form>
      </div>
      {error && <p role="alert" className="mt-3 text-sm text-red-800">{error}</p>}
      {result && result.data.length === 0 && <p className="mt-3 text-sm text-gray-600">No work order batches yet.</p>}
      {result && result.data.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="responsive-table w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-600">
                <th className="py-2 pr-3">Date</th><th className="py-2 pr-3">State</th><th className="py-2 pr-3">Jobs confirmed</th>
                <th className="py-2 pr-3">Copies</th><th className="py-2 pr-3">Prepared</th><th className="py-2 pr-3">Last confirmed</th><th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {result.data.map((b) => (
                <tr key={b.id} className="border-b border-gray-100">
                  <td data-label="Date" data-primary className="py-2 pr-3">{formatJobDate(b.scheduledDate)}</td>
                  <td data-label="State" className="py-2 pr-3">{batchStatusLabel(b.status)}</td>
                  <td data-label="Jobs confirmed" className="py-2 pr-3">{b.confirmedJobCount} of {b.jobCount}</td>
                  <td data-label="Copies" className="py-2 pr-3">{b.serviceCopyCount} service · {b.billingCopyCount} billing</td>
                  <td data-label="Prepared" className="py-2 pr-3">{b.preparedByEmail}, {new Date(b.createdAt).toLocaleString()}</td>
                  <td data-label="Last confirmed" className="py-2 pr-3">{b.lastConfirmedAt ? `${b.lastConfirmedByEmail ?? ''}, ${new Date(b.lastConfirmedAt).toLocaleString()}` : '—'}</td>
                  <td className="py-2"><Link to={`/work-orders?batchId=${encodeURIComponent(b.id)}`} className={rowActionClass}>Open</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 flex items-center gap-3 text-sm">
            <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => setQuery((q) => ({ ...q, page: page - 1 }))}>Previous</button>
            <span>Page {page} of {Math.max(1, result.pagination.pages)}</span>
            <button type="button" className={secondaryButtonClass} disabled={page >= result.pagination.pages} onClick={() => setQuery((q) => ({ ...q, page: page + 1 }))}>Next</button>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Batch preview, printing and confirmation
// ---------------------------------------------------------------------------

/** True once the logo loaded or failed (or after LOGO_WAIT_MS), so Print never waits forever. */
function useLogoReady(url: string): boolean {
  const [ready, setReady] = useState(!url);
  useEffect(() => {
    if (!url) {
      setReady(true);
      return undefined;
    }
    setReady(false);
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      setReady(true);
    };
    const image = new Image();
    image.onload = finish;
    image.onerror = finish;
    image.src = url;
    const timer = window.setTimeout(finish, LOGO_WAIT_MS);
    return () => {
      finished = true;
      window.clearTimeout(timer);
    };
  }, [url]);
  return ready;
}

type ConfirmPhase = 'idle' | 'choose' | 'review';

function WorkOrderBatchView({ batchId }: { batchId: string }) {
  const api = useMemo(() => createAPIClient(), []);
  const [batch, setBatch] = useState<types.WorkOrderBatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [included, setIncluded] = useState<Set<string>>(new Set());
  const [run, setRun] = useState<Map<string, string> | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [phase, setPhase] = useState<ConfirmPhase>('idle');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [problems, setProblems] = useState<types.WorkOrderJobProblem[]>([]);
  const [result, setResult] = useState<types.ConfirmWorkOrderJobsResult | null>(null);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const detail = await api.getWorkOrderBatch(batchId);
      setBatch(detail);
      if (firstLoad.current) {
        firstLoad.current = false;
        setIncluded(defaultPrintRun(detail.members));
      } else {
        // Keep staff's choice, minus jobs that can no longer print
        setIncluded((current) => new Set(Array.from(current).filter((id) => detail.members.some((m) => m.jobId === id && m.job))));
      }
    } catch (error) {
      setBatch(null);
      setLoadError(getErrorMessage(error, 'Failed to load work orders'));
    } finally {
      setLoading(false);
    }
  }, [api, batchId]);

  useEffect(() => { void load(); }, [load]);

  const logoUrl = batch?.company.logoUrl?.trim() || '';
  const logoReady = useLogoReady(logoUrl);
  const members = useMemo(() => batch?.members ?? [], [batch]);
  const sheets = useMemo(() => expandSheets(members, included), [members, included]);
  const blockers = printBlockers(members, included);
  const groups = groupByRouteAndTechnician(members, memberGroupKey);
  const canPrint = !!batch && !loading && logoReady && sheets.length > 0 && blockers.length === 0 && !busy;
  const nameOf = (jobId: string) => jobLabel(members.find((m) => m.jobId === jobId)?.job, jobId);

  const toggleIncluded = (jobId: string) => setIncluded((current) => {
    const next = new Set(current);
    if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
    return next;
  });

  const print = async () => {
    if (!batch || !canPrint) return;
    const versions = printedVersions(members, included);
    setBusy(true);
    setActionError(null);
    setProblems([]);
    setResult(null);
    try {
      await api.recordWorkOrderPrint(batch.id, Array.from(versions.keys()));
    } catch (error) {
      setActionError(getErrorMessage(error, 'Could not record the print; nothing was sent to the printer'));
      setBusy(false);
      return;
    }
    setBusy(false);
    window.print();
    // The browser cannot tell what printed: staff ticks it, nothing preselected
    setRun(versions);
    setTicked(new Set());
    setPhase('choose');
  };

  const confirm = async () => {
    if (!batch || !run) return;
    const body = confirmRequest(Array.from(ticked), run);
    if (body.length === 0) return;
    setBusy(true);
    setActionError(null);
    setProblems([]);
    try {
      const outcome = await api.confirmWorkOrderJobs(batch.id, body);
      setResult(outcome);
      setPhase('idle');
      setRun(null);
      setTicked(new Set());
      await load();
    } catch (error) {
      const found = problemsFromError(error);
      setActionError(found.length > 0
        ? 'Nothing was confirmed and no job status changed. Review the jobs below, reprint any that changed, then confirm again.'
        : getErrorMessage(error, 'Failed to confirm'));
      setProblems(found);
      setTicked((current) => new Set(Array.from(current).filter((id) => !found.some((p) => p.jobId === id))));
      // Their printed version is stale or they need review: reprint before confirming them
      setRun((current) => (current ? new Map(Array.from(current).filter(([id]) => !found.some((p) => p.jobId === id))) : current));
      setPhase('choose');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const confirmable = run ? confirmableInRun(members, run) : [];
  const tickedList = Array.from(ticked).filter((id) => confirmable.some((m) => m.jobId === id));

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 print:m-0 print:max-w-none print:p-0">
      <div className="print:hidden">
        <Link to="/work-orders" className="inline-block py-2 text-sm text-blue-700 hover:underline sm:py-0">← Work Orders</Link>
        <h1 className="mt-2 text-2xl font-bold text-gray-900">
          Work orders{batch ? ` for ${formatJobDate(batch.scheduledDate)}` : ''}
        </h1>

        {loading && !batch && <p role="status" className="mt-4 text-gray-600">Loading work orders…</p>}
        {loadError && (
          <div role="alert" className="mt-4 text-sm text-red-800">
            {loadError} Nothing can be printed until every work order loads.{' '}
            <button type="button" className={rowActionClass} onClick={() => void load()}>Retry</button>
          </div>
        )}

        {batch && (
          <>
            <p className="mt-1 text-sm text-gray-700" data-testid="batch-summary">
              {batchStatusLabel(batch.status)} · {batch.confirmedJobCount} of {pluralize(batch.jobCount, 'job')} confirmed printed ·{' '}
              {batch.serviceCopyCount} service and {batch.billingCopyCount} billing copies · prepared by {batch.preparedByEmail}
              {batch.lastConfirmedAt ? ` · last confirmed by ${batch.lastConfirmedByEmail ?? 'unknown'} ${new Date(batch.lastConfirmedAt).toLocaleString()}` : ''}
            </p>

            {result && (
              <div role="status" className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">
                <p className="font-semibold">Confirmed. {confirmationSummary(result)}</p>
                {result.newlyGenerated.length > 0 && (
                  <p className="mt-1">
                    Now Work Order Generated:{' '}
                    {result.newlyGenerated.map((id, i) => (
                      <React.Fragment key={id}>{i > 0 && ', '}<Link to={`/jobs/${encodeURIComponent(id)}`} className="underline">{nameOf(id)}</Link></React.Fragment>
                    ))}
                  </p>
                )}
              </div>
            )}

            {phase !== 'idle' && run && (
              <section aria-label="Which jobs printed" className="mt-4 rounded-lg border-2 border-blue-300 bg-white p-4">
                <h2 className="text-lg font-semibold text-gray-900">Which jobs printed?</h2>
                <p className="mt-1 text-sm text-gray-700">
                  The browser cannot tell whether the printer finished. Tick only the jobs whose requested copies all printed.
                  Jobs left unticked keep their status and can be printed and confirmed later from this batch.
                </p>
                {phase === 'choose' && (
                  <>
                    {confirmable.length === 0 ? (
                      <p className="mt-3 text-sm text-gray-700">Every job in this print run is already confirmed or needs review. Reprints need no confirmation.</p>
                    ) : (
                      <ul className="mt-3 space-y-2">
                        {confirmable.map((m) => (
                          <li key={m.jobId}>
                            <label className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox" className="h-5 w-5"
                                checked={ticked.has(m.jobId)}
                                onChange={() => setTicked((current) => {
                                  const next = new Set(current);
                                  if (next.has(m.jobId)) next.delete(m.jobId); else next.add(m.jobId);
                                  return next;
                                })}
                              />
                              <span>{jobLabel(m.job, m.jobId)} ({pluralize(m.serviceCopies, 'service copy', 'service copies')}, {pluralize(m.billingCopies, 'billing copy', 'billing copies')})</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-3 flex flex-wrap gap-3">
                      {confirmable.length > 0 && (
                        <button type="button" className={secondaryButtonClass} onClick={() => setTicked(new Set(confirmable.map((m) => m.jobId)))}>
                          Tick all listed
                        </button>
                      )}
                      <button type="button" className={primaryButtonClass} disabled={tickedList.length === 0} onClick={() => setPhase('review')}>
                        Continue with {pluralize(tickedList.length, 'job')}
                      </button>
                      <button type="button" className={secondaryButtonClass} onClick={() => { setPhase('idle'); setRun(null); setTicked(new Set()); }}>
                        Nothing more printed
                      </button>
                    </div>
                  </>
                )}
                {phase === 'review' && (
                  <div className="mt-3">
                    <p className="text-sm font-semibold">Confirm that these {pluralize(tickedList.length, 'job')} printed:</p>
                    <ul className="mt-1 list-disc pl-5 text-sm">
                      {tickedList.map((id) => <li key={id}>{nameOf(id)}</li>)}
                    </ul>
                    <p className="mt-2 text-sm text-gray-700">Scheduled jobs among them move to Work Order Generated. Others keep their status.</p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => void confirm()}>
                        {busy ? 'Confirming…' : `Confirm ${pluralize(tickedList.length, 'job')} printed`}
                      </button>
                      <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setPhase('choose')}>Back</button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {actionError && <p role="alert" className="mt-3 text-sm text-red-800">{actionError}</p>}
            <ProblemList problems={problems} title="These jobs need review:" />

            <section aria-label="Print run" className={`${sectionClass} mt-4`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Print run</h2>
                <div className="flex flex-wrap gap-2">
                  <button type="button" className={secondaryButtonClass} onClick={() => setIncluded(new Set(members.filter((m) => m.job && !m.confirmedAt).map((m) => m.jobId)))}>
                    Unconfirmed only
                  </button>
                  <button type="button" className={secondaryButtonClass} onClick={() => setIncluded(new Set(members.filter((m) => m.job).map((m) => m.jobId)))}>
                    All jobs
                  </button>
                  <button type="button" className={secondaryButtonClass} disabled={loading} onClick={() => void load()}>
                    Refresh details
                  </button>
                </div>
              </div>
              <p className="mt-1 text-sm text-gray-600">
                Each copy prints on its own sheet with the job&apos;s current details. Jobs already confirmed print with a REPRINT label.
              </p>
              {groups.map((group) => (
                <div key={group.key} className="mt-3">
                  <h3 className="text-sm font-semibold text-gray-800">{group.route} · {group.technician}</h3>
                  <ul className="mt-1 divide-y divide-gray-100 rounded border border-gray-200">
                    {group.items.map((m) => (
                      <li key={m.jobId} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                        <input
                          type="checkbox" className="h-5 w-5"
                          checked={included.has(m.jobId)}
                          disabled={!m.job}
                          onChange={() => toggleIncluded(m.jobId)}
                          aria-label={`Include ${jobLabel(m.job, m.jobId)} in this print run`}
                        />
                        <span className="w-6 text-gray-500">{m.position}.</span>
                        <span className="min-w-0 flex-1 break-words">
                          {m.job
                            ? <Link to={`/jobs/${encodeURIComponent(m.jobId)}`} className="font-medium text-blue-700 hover:underline">{jobLabel(m.job, m.jobId)}</Link>
                            : <span className="font-medium text-red-800">Job {m.jobId} no longer exists</span>}
                          {m.job && <span className="text-gray-600"> · {timeLabel(m.job.scheduledStartTime)} · {jobStatusLabel(m.job.status)}</span>}
                        </span>
                        <span className="text-gray-700">{m.serviceCopies} service · {m.billingCopies} billing</span>
                        {m.reprint && <span className={`${badgeClass} bg-amber-100 text-amber-900`}>REPRINT</span>}
                        {m.confirmedAt
                          ? <span className={`${badgeClass} bg-green-100 text-green-900`}>Confirmed{m.confirmedByEmail ? ` by ${m.confirmedByEmail}` : ''}</span>
                          : <span className={`${badgeClass} bg-gray-100 text-gray-800`}>Not confirmed</span>}
                        {m.confirmBlock && <span className="basis-full text-red-700">{confirmBlockMessage(m.confirmBlock)}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {blockers.length > 0 && (
                <p role="alert" className="mt-3 text-sm text-red-800">
                  {pluralize(blockers.length, 'job')} in this print run could not be loaded. Exclude {blockers.length === 1 ? 'it' : 'them'} before printing so no work order is silently missing.
                </p>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button type="button" className={primaryButtonClass} disabled={!canPrint} onClick={() => void print()}>
                  Print {pluralize(sheets.length, 'sheet')}
                </button>
                {batch && !logoReady && <span className="text-sm text-gray-600">Loading the company logo…</span>}
                {sheets.length === 0 && <span className="text-sm text-gray-600">Choose at least one job to print.</span>}
              </div>
            </section>

            <h2 className="mt-6 mb-2 text-lg font-semibold text-gray-900">Preview</h2>
          </>
        )}
      </div>

      {batch && blockers.length === 0 && (
        <div className="work-order-documents" aria-label="Work order documents">
          {sheets.map((sheet, index) => (
            <article
              key={sheet.key}
              data-copy={sheet.copy}
              className={`print-sheet mx-auto mb-6 max-w-[8.5in] bg-white px-4 py-6 text-sm text-gray-900 shadow sm:px-10 sm:py-10${index > 0 ? ' print-new-sheet' : ''}`}
            >
              <CompanyLetterhead company={batch.company} />
              <ServiceOrderDocument job={sheet.job} copy={sheet.copy} reprint={sheet.member.reprint} checklist={sheet.member.checklist} />
              <p className="mt-6 text-xs text-gray-600">
                Work order {sheet.member.position} of {batch.jobCount} · {sheet.copy === 'SERVICE' ? 'Service' : 'Billing'} copy {sheet.index} of {sheet.of}
              </p>
              <CompanyFooterNote company={batch.company} />
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
